import { motion } from 'framer-motion'

/**
 * AppMockup — A pure CSS recreation of the Cricket Content Editor desktop tool.
 * Used as a visual hero element to showcase what the tool looks like.
 */
export default function AppMockup({ className = '', compact = false }) {
  const playerBags = [
    { name: 'Virat K.', color: '#3b82f6', clips: 8 },
    { name: 'Rohit S.', color: '#ef4444', clips: 5 },
    { name: 'Jasprit B.', color: '#f59e0b', clips: 6 },
    { name: 'Hardik P.', color: '#8b5cf6', clips: 4 },
  ]

  const timelineClips = [
    { start: 5, width: 12, color: '#3b82f6', label: 'Cover Drive' },
    { start: 22, width: 8, color: '#3b82f6', label: 'Pull Shot' },
    { start: 38, width: 15, color: '#ef4444', label: 'Six' },
    { start: 58, width: 10, color: '#f59e0b', label: 'Yorker' },
    { start: 75, width: 12, color: '#8b5cf6', label: 'Catch' },
  ]

  return (
    <div className={`rounded-2xl overflow-hidden app-mockup-shadow border border-white/10 bg-[#111111] ${className}`}>
      {/* Title Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#1a1a1a] border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
          </div>
          <span className="text-[11px] text-gray-500 ml-3 font-medium">Reel Cricket Editor</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-2 py-0.5 bg-pitch-green/20 rounded text-[9px] text-pitch-green font-bold">BETA</div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex ${compact ? 'h-48' : 'h-72 md:h-80'}`}>
        {/* Left Panel — Player Bags */}
        <div className={`${compact ? 'w-36' : 'w-44 md:w-52'} border-r border-white/5 bg-[#141414] flex flex-col`}>
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Player Bags</span>
            <motion.button
              whileHover={{ scale: 1.1 }}
              className="w-5 h-5 rounded bg-pitch-green/20 flex items-center justify-center text-pitch-green text-[10px] font-bold"
            >
              +
            </motion.button>
          </div>
          <div className="flex-1 overflow-hidden p-2 space-y-1.5">
            {playerBags.map((player, i) => (
              <motion.div
                key={player.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + i * 0.15 }}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all text-[11px] ${
                  i === 0
                    ? 'bg-white/10 border border-white/10'
                    : 'hover:bg-white/5'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: player.color }}
                />
                <span className="text-gray-300 truncate font-medium">{player.name}</span>
                <span className="ml-auto text-gray-600 text-[9px] font-mono">{player.clips}</span>
              </motion.div>
            ))}

            {/* Unassigned */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.5 }}
              className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-[11px] hover:bg-white/5 cursor-pointer mt-2 border-t border-white/5 pt-3"
            >
              <div className="w-2.5 h-2.5 rounded-full bg-gray-600 flex-shrink-0" />
              <span className="text-gray-500 truncate">Unassigned</span>
              <span className="ml-auto text-gray-600 text-[9px] font-mono">12</span>
            </motion.div>
          </div>
        </div>

        {/* Center — Video Preview */}
        <div className="flex-1 flex flex-col bg-[#0d0d0d]">
          {/* Video Area */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden">
            {/* Fake video frame with cricket-themed gradient */}
            <div className={`${compact ? 'w-20 h-36' : 'w-28 h-48 md:w-32 md:h-56'} bg-gradient-to-br from-green-900/40 via-gray-900 to-green-800/30 rounded-lg border border-white/10 relative overflow-hidden`}>
              {/* 9:16 label */}
              <div className="absolute top-1.5 right-1.5 bg-black/60 px-1.5 py-0.5 rounded text-[7px] text-pitch-green font-bold">
                9:16
              </div>
              {/* Fake cricket field lines */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border border-green-500/20 rounded-full" />
                <div className="absolute w-12 h-16 border border-green-500/10 rounded-lg" />
              </div>
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-8 h-8 bg-pitch-green/30 rounded-full flex items-center justify-center backdrop-blur-sm"
                >
                  <svg className="w-3.5 h-3.5 text-pitch-green ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Playback Controls */}
          <div className="px-3 py-2 border-t border-white/5 flex items-center gap-3">
            <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
            <div className="w-4 h-4 rounded-full bg-pitch-green/30 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-pitch-green" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
            <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden mx-2">
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: '45%' }}
                transition={{ duration: 2, delay: 1 }}
                className="h-full bg-pitch-green rounded-full"
              />
            </div>
            <span className="text-[9px] text-gray-600 font-mono">01:23 / 03:15</span>
          </div>
        </div>

        {/* Right Panel — Timestamps (only in non-compact mode) */}
        {!compact && (
          <div className="w-40 md:w-48 border-l border-white/5 bg-[#141414] flex-col hidden md:flex">
            <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Timestamps</span>
              <span className="text-[9px] text-pitch-green font-mono">5 clips</span>
            </div>
            <div className="flex-1 overflow-hidden p-2 space-y-1">
              {[
                { time: '00:12', label: 'Cover Drive', color: '#3b82f6' },
                { time: '00:45', label: 'Pull Shot', color: '#3b82f6' },
                { time: '01:23', label: 'Six Over Long-on', color: '#ef4444' },
                { time: '02:01', label: 'Yorker Wicket', color: '#f59e0b' },
                { time: '02:45', label: 'Diving Catch', color: '#8b5cf6' },
              ].map((ts, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 + i * 0.12 }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 transition-all text-[10px] cursor-pointer"
                >
                  <span className="text-gray-500 font-mono w-8">{ts.time}</span>
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: ts.color }} />
                  <span className="text-gray-400 truncate">{ts.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Bar */}
      <div className="border-t border-white/5 bg-[#141414] px-3 py-2">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[9px] text-gray-500 font-mono">00:00</span>
          <div className="flex-1 relative h-6 bg-[#1a1a1a] rounded overflow-hidden border border-white/5">
            {/* Timeline clips */}
            {timelineClips.map((clip, i) => (
              <motion.div
                key={i}
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ delay: 1.2 + i * 0.15, duration: 0.4 }}
                className="absolute top-1 bottom-1 rounded-sm origin-left"
                style={{
                  left: `${clip.start}%`,
                  width: `${clip.width}%`,
                  backgroundColor: clip.color + '40',
                  borderLeft: `2px solid ${clip.color}`,
                }}
              >
                <span className="text-[7px] text-white/60 px-1 truncate block leading-4">{clip.label}</span>
              </motion.div>
            ))}
            {/* Playhead */}
            <motion.div
              initial={{ left: '0%' }}
              animate={{ left: '45%' }}
              transition={{ duration: 2, delay: 1 }}
              className="absolute top-0 bottom-0 w-0.5 bg-pitch-green z-10"
            >
              <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-pitch-green rounded-full" />
            </motion.div>
          </div>
          <span className="text-[9px] text-gray-500 font-mono">03:15</span>
        </div>
        {/* Action buttons */}
        <div className="flex items-center gap-2 justify-end">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="px-3 py-1 bg-pitch-green/20 rounded text-[9px] text-pitch-green font-bold cursor-pointer hover:bg-pitch-green/30 transition-colors"
          >
            Export All Reels
          </motion.div>
          <div className="px-2 py-1 bg-white/5 rounded text-[9px] text-gray-500 cursor-pointer hover:bg-white/10 transition-colors">
            Copy All
          </div>
        </div>
      </div>
    </div>
  )
}
